import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Button, Snackbar, Alert, Typography } from '@mui/material';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { useDispatch, useSelector } from 'react-redux';
import KanbanBoard from '../../components/apps/kanban/KanbanBoard';
import CreateColumnModal from '../../components/apps/kanban/CreateColumnModal';
import KanbanDateFilter from '../../components/apps/kanban/KanbanDateFilter';
import { getColumnsPaginated, createColumn } from '../../reducers/kanban/columnActions';
import { useToken } from '../../hooks/useToken';
import { GET_COLUMNS_ERROR } from '../../reducers/kanban/kanbanTypes';
import TicketDetailModal from '../../components/apps/kanban/TicketDetailModal';

/**
 * Vista Kanban para tableros de soporte técnico
 */
const SupportKanbanView = ({ boardId, isDetailView = false, board: initialBoardData = null }) => {
  const dispatch = useDispatch();
  const { getTokenSilent } = useToken();

  const { items: allTicketsForBoard = [] } = useSelector(state => state.kanban.tickets);
  const { items: columns = [], loading: columnsLoading } = useSelector(state => state.kanban.columns);

  const ticketsState = useSelector(state => state.kanban.tickets);
  const dateFilters = useMemo(() => ticketsState?.filters || {}, [ticketsState?.filters]);

  const [createColumnModalOpen, setCreateColumnModalOpen] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Estados para el modal de detalles del ticket
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [ticketIdToOpenDetails, setTicketIdToOpenDetails] = useState(null);

  const prevBoardIdRef = useRef(null);
  const showCreateColumnButton = false;
  // Referencia al KanbanBoard para recargar tickets
  const kanbanBoardRef = useRef(null);

  const showNotification = useCallback((message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  }, []);

  const handleCloseNotification = () => {
    setNotification(prev => ({
      ...prev,
      open: false
    }));
  };

  useEffect(() => {
    if (!boardId) return;

    if (prevBoardIdRef.current === boardId) {
      return;
    }

    prevBoardIdRef.current = boardId;
    let isMounted = true;

    const fetchColumns = async () => {
      try {
        const state = dispatch({
          type: 'GET_STATE'
        });

        const kanbanState = state?.getState?.()?.kanban || {};
        const currentColumns = kanbanState.columns?.items || [];
        const existingColumns = currentColumns.filter(col => String(col.board_id) === String(boardId));

        if (existingColumns.length > 0) {
          return;
        }

        const token = await getTokenSilent();

        if (!token) {
          throw new Error('No se pudo obtener el token de autenticación');
        }

        // Asegurarnos de que el token tenga el formato correcto
        const formattedToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;

              // Usar el boardId directamente, la función getColumnsPaginated se encargará de procesarlo si es necesario
      const resultPromise = dispatch(getColumnsPaginated(boardId, 0, 5, formattedToken));

        resultPromise.then(result => {
          if (isMounted && result && result.error) {
            showNotification(`Error: ${result.error}`, 'error');
          }
        });
      } catch (error) {
        if (!isMounted) return;

        console.error('Error al cargar columnas:', error);

        dispatch({
          type: GET_COLUMNS_ERROR,
          payload: error.message || 'Error al cargar las columnas del tablero'
        });

        showNotification('Error al cargar las columnas: ' + (error.message || 'Error desconocido'), 'error');
      }
    };

    fetchColumns();

    return () => {
      isMounted = false;
    };
  }, [boardId, dispatch, getTokenSilent, showNotification]);

  useEffect(() => {
    if (ticketIdToOpenDetails) {
      const ticketToOpen = allTicketsForBoard.find(t => String(t.id) === String(ticketIdToOpenDetails));

      if (ticketToOpen) {
        setSelectedTicket(ticketToOpen);
        setDetailModalOpen(true);
      } else {
        console.warn(`No se encontró el ticket con ID ${ticketIdToOpenDetails}`);
      }
      setTicketIdToOpenDetails(null);
    }
  }, [ticketIdToOpenDetails, allTicketsForBoard]);

  const formattedColumns = useMemo(() => {
    const supportColumns = columns.filter(column => {
      try {
        const belongsToBoard = String(column.board_id) === String(boardId);
        return belongsToBoard;

      } catch (error) {
        console.error('Error al procesar metadatos de columna:', error, column);
        return false;
      }
    });

    const sortedColumns = [...supportColumns].sort((colA, colB) => {
      const hasPositionA = colA.position !== undefined && colA.position !== null && !isNaN(Number(colA.position));
      const hasPositionB = colB.position !== undefined && colB.position !== null && !isNaN(Number(colB.position));
      if (!hasPositionA && !hasPositionB) return 0;
      if (!hasPositionA) return 1;
      if (!hasPositionB) return -1;
      return Number(colA.position) - Number(colB.position);
    });

    return sortedColumns.map(column => ({
      id: column.id.toString(),
      name: column.name,
      position: column.position,
      tickets: allTicketsForBoard.filter(ticket => String(ticket.column_id) === String(column.id)) || [],
      meta_data: column.meta_data,
      board_id: column.board_id
    }));
  }, [boardId, columns, allTicketsForBoard]);

  const prevFiltersRef = useRef(null);
  useEffect(() => {
    let isMounted = true;
    const prevFilters = prevFiltersRef.current;
    const currentFiltersStr = JSON.stringify(dateFilters);
    const prevFiltersStr = prevFilters ? JSON.stringify(prevFilters) : null;

    if (prevFiltersStr === currentFiltersStr) return;
    prevFiltersRef.current = dateFilters;

    if (isMounted) {
      if (dateFilters.startDate || dateFilters.endDate) {
        const filterMessage = dateFilters.startDate && dateFilters.endDate
          ? `Mostrando tickets del ${dateFilters.startDate} al ${dateFilters.endDate}`
          : dateFilters.startDate
            ? `Mostrando tickets desde el ${dateFilters.startDate}`
            : `Mostrando tickets hasta el ${dateFilters.endDate}`;
        showNotification(filterMessage, 'info');
      } else if (prevFilters && (prevFilters.startDate || prevFilters.endDate)) {
        showNotification('Filtros eliminados. Mostrando todos los tickets.', 'success');
      }
    }
    return () => { isMounted = false; };
  }, [dateFilters, showNotification]);

  const handleCreateColumn = () => {
    setCreateColumnModalOpen(true);
  };

  const handleSaveColumn = async (columnName) => {
    setCreateColumnModalOpen(false);
    try {
      const token = await getTokenSilent();
      if (!token || !boardId || !columnName) {
          showNotification('Faltan datos para crear la columna.', 'error');
          return;
      }

      const newPosition = columns.filter(col => String(col.board_id) === String(boardId)).length;

      // Añadir flag para identificar creación manual
      dispatch(createColumn({ board_id: boardId, name: columnName, position: newPosition, isManualCreation: true }, token))
        .then(() => {
          showNotification('Columna creada exitosamente.', 'success');
          // No recargar columnas, createColumn ya actualizó el estado de Redux
                      // Log de éxito eliminado
        })
        .catch(error => {
          showNotification(`Error al crear columna: ${error.message}`, 'error');
        });
    } catch (error) {
      showNotification(`Error al crear columna: ${error.message}`, 'error');
    }
  };

  const handleTicketClick = useCallback((ticket) => {
    if (ticket && ticket.id) {
      setTicketIdToOpenDetails(ticket.id);
    } else {
      console.warn('Ticket inválido o sin ID');
    }
  }, []);

  const handleCloseDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedTicket(null);
  };

  const handleTicketCreationSuccess = useCallback((ticket) => {
                // Log de éxito eliminado

    // Mostrar notificación de éxito
    showNotification(`Ticket "${ticket.name}" creado exitosamente en ${ticket.column_name}`, 'success');

    // Recargar la columna donde se creó el ticket
    if (kanbanBoardRef.current && ticket.column_id) {
      setTimeout(() => {
        kanbanBoardRef.current.reloadColumnTickets(ticket.column_id);
      }, 500); // Pequeño retraso para asegurar que el servidor ha procesado la creación
    }
  }, [showNotification]);

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: 'background.default'
    }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: 2,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="subtitle1" fontWeight="bold">
          {initialBoardData?.name || 'Tablero Kanban de Soporte'}
        </Typography>

        {showCreateColumnButton && (
          <Button
            variant="contained"
            color="secondary"
            size="small"
            startIcon={<ViewColumnIcon />}
            onClick={handleCreateColumn}
          >
            Añadir Columna
          </Button>
        )}
      </Box>

      <Box sx={{ px: 2, py:1 }}>
        <KanbanDateFilter />
      </Box>

      <Box sx={{ flexGrow: 1, overflow: 'hidden', position: 'relative', p: 2 }}>
        {columnsLoading && formattedColumns.length === 0 ? (
            <Typography sx={{textAlign: 'center', mt: 4}}>Cargando columnas...</Typography>
        ) : !columnsLoading && formattedColumns.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: 'calc(100% - 40px)', gap: 2 }}>
            <Typography variant="h6" color="textSecondary">
              Este tablero no tiene columnas o no se pudieron cargar.
            </Typography>
          </Box>
        ) : (
          <KanbanBoard
            ref={kanbanBoardRef}
            boardType={initialBoardData?.board_type === 'support' ? 'soporte' : (initialBoardData?.board_type || "soporte")}
            columns={formattedColumns}
            onTicketClick={handleTicketClick}
            onTicketCreated={handleTicketCreationSuccess}
          />
        )}
      </Box>

      <CreateColumnModal
        open={createColumnModalOpen}
        onClose={() => setCreateColumnModalOpen(false)}
        onSave={handleSaveColumn}
        boardId={boardId}
      />

      {detailModalOpen && selectedTicket && (
        <TicketDetailModal
          open={detailModalOpen}
          onClose={handleCloseDetailModal}
          ticket={selectedTicket}
          boardId={boardId}
        />
      )}

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SupportKanbanView;